import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import Badge from '../../components/ui/Badge';
import { UserProfileSummaryData, SummaryEducationEntry, SummaryExperienceEntry, SummarySkill, SummaryAttachment } from '../../types/components';
import { useJobSeeker } from '../../context/JobSeekerContext';
import { useAuth } from '../../context/AuthContext';
import { UploadFileType } from '../../types/api';

const UserProfileSummary: FC = () => {
  const { jobSeekerDetails, loadingState } = useJobSeeker();
  const { user } = useAuth();

  const profileData: UserProfileSummaryData | null = useMemo(() => {
    if (!jobSeekerDetails || !user) {
      return null;
    }

    const profilePhotoAttachment = jobSeekerDetails.attachments?.find(
      (att) => att.typeId === "ProfilePhoto"
    );
    const resumeAttachment = jobSeekerDetails.attachments?.find(
      (att) => att.typeId === "Resume"
    );
    const coverLetterAttachment = jobSeekerDetails.attachments?.find(
      (att) => att.typeId === "CoverLetter"
    );
    const workSampleAttachments = jobSeekerDetails.attachments?.filter(
      (att) => att.typeId === "PostAttachment" // Assuming work samples are PostAttachment type
    );

    return {
      userId: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobileNumber: user.mobileNumber,
      profilePhotoUrl: profilePhotoAttachment?.fileUrl || null,
      address: jobSeekerDetails.jobSeekerBasicDetails?.address,
      city: jobSeekerDetails.jobSeekerPreferredLocationDetails?.[0]?.cityName, // Assuming first preferred location
      // state: jobSeekerDetails.jobSeekerPreferredLocationDetails?.[0]?.stateName, // Removed as stateName is not available
      education: jobSeekerDetails.jobSeekerQualificationDetails || [],
      experience: jobSeekerDetails.jobSeekerExperienceDetails || {},
      skills: jobSeekerDetails.jobSeekerSkillDetails?.map(skill => ({
        skillId: skill.skillId,
        skillLevel: skill.skillLevel,
        // skillName will need to be looked up if needed for display
      })) || [],
      resumeUrl: resumeAttachment?.fileUrl,
      coverLetterUrl: coverLetterAttachment?.fileUrl,
      workSampleUrls: workSampleAttachments?.map(ws => ({
        attachmentId: ws.attachmentId,
        typeId: ws.typeId,
        fileUrl: ws.fileUrl,
        originalName: ws.originalName,
      })) || [],
      isProfileComplete: jobSeekerDetails.isProfileComplete,
      profileCompletionPercentage: jobSeekerDetails.isProfileComplete ? 100 : 0, // Placeholder, actual logic might be more complex
    };
  }, [jobSeekerDetails, user]);

  if (loadingState.fetchData || !profileData) {
    return <div>Loading profile...</div>; // Or a more sophisticated loader
  }

  const getAvailabilityText = (isFresher?: boolean, yearsOfExperience?: number): string => {
    if (isFresher) return 'Fresher';
    if (yearsOfExperience !== undefined) return `${yearsOfExperience}+ years experience`;
    return 'Not specified';
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Profile</h1>
            <p className="text-gray-600">Complete overview of your professional profile</p>
          </div>
          <div className="flex space-x-3">
            <Link to="/user/profile/edit">
              <Button variant="outline">Edit Profile</Button>
            </Link>
            {profileData.resumeUrl && (
              <a href={profileData.resumeUrl} target="_blank" rel="noopener noreferrer">
                <Button>Download Resume</Button>
              </a>
            )}
          </div>
        </div>
        
        {/* Profile Completion */}
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Profile Completion</h2>
            <span className="text-sm text-gray-500">{profileData.profileCompletionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen h-3 rounded-full transition-all duration-500"
              style={{ width: `${profileData.profileCompletionPercentage}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            {(profileData.profileCompletionPercentage || 0) < 100 
              ? 'Complete the remaining sections to increase your profile visibility'
              : 'Your profile is complete and visible to recruiters!'
            }
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Personal Info & Contact */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <Avatar
                name={`${profileData.firstName || ''} ${profileData.lastName || ''}`}
                size="2xl"
                src={profileData.profilePhotoUrl}
                className="mx-auto mb-4"
              />
              <h2 className="text-xl font-bold text-gray-900">{profileData.firstName} {profileData.lastName}</h2>
              {profileData.experience && (
                <Badge variant="primary" className="mb-4">
                  {getAvailabilityText(profileData.experience.isFresher, profileData.experience.yearsOfExperience)}
                </Badge>
              )}
            </div>
            
            <div className="space-y-4">
              {profileData.email && (
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-600">{profileData.email}</span>
                </div>
              )}
              {profileData.mobileNumber && (
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm text-gray-600">{profileData.mobileNumber}</span>
                </div>
              )}
              {profileData.city && (
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    {profileData.city}
                  </span>
                </div>
              )}
            </div>

            {/* Social Links - Removed as per plan */}
          </div>

          {/* Skills */}
          {profileData.skills && profileData.skills.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
              <div className="flex flex-wrap gap-2">
                {profileData.skills.map((skill, index) => (
                  <Badge key={index} variant="secondary" size="sm">
                    {/* Assuming skillName is available or can be looked up */}
                    {skill.skillId} - {skill.skillLevel} 
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Languages - Removed as per plan */}
        </div>

        {/* Right Column - Professional Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Career Objective - Removed as per plan */}

          {/* Work Experience */}
          {profileData.experience && (profileData.experience.isFresher || profileData.experience.yearsOfExperience || profileData.experience.currentCTC) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Experience</h3>
              <div className="space-y-6">
                <div className="border-l-4 border-primary-lightGreen pl-4">
                  {profileData.experience.isFresher ? (
                    <p className="text-md font-semibold text-gray-900">Fresher</p>
                  ) : (
                    <>
                      <h4 className="text-md font-semibold text-gray-900">{profileData.experience.yearsOfExperience} Years Experience</h4>
                      {profileData.experience.currentCTC && (
                        <p className="text-sm text-gray-600">Current CTC: {profileData.experience.currentCTC}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Education */}
          {profileData.education && profileData.education.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
              <div className="space-y-4">
                {profileData.education.map((edu, index) => (
                  <div key={index} className="border-l-4 border-primary-teaGreen pl-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="text-md font-semibold text-gray-900">
                          {edu.customCourseName || `Course ID: ${edu.courseId}`}
                        </h4>
                        {edu.isCompleted !== null && (
                          <p className="text-sm text-gray-600">
                            Status: {edu.isCompleted ? 'Completed' : 'In Progress'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications - Removed as per plan */}

          {/* Job Preferences - Removed as per plan */}

          {/* Documents */}
          {(profileData.resumeUrl || (profileData.workSampleUrls && profileData.workSampleUrls.length > 0)) && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents & Portfolio</h3>
              <div className="space-y-4">
                {profileData.resumeUrl && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-700">Resume</span>
                    </div>
                    <a href={profileData.resumeUrl} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline">Download</Button>
                    </a>
                  </div>
                )}
                
                {profileData.workSampleUrls && profileData.workSampleUrls.map((sample, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5 text-primary-lightGreen" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-700">{sample.originalName || `Work Sample ${index + 1}`}</span>
                    </div>
                    <a href={sample.fileUrl} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline">View</Button>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileSummary;
